/* Installers Near Me (sections/installer-locator.liquid). No geocoding service: a 5-digit ZIP
   is placed at the average position of listed installers sharing its first 3 digits. */
(() => {
  if (customElements.get('installer-locator')) return;

  const LEAFLET = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/';
  const STATES = {
    alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA', colorado: 'CO',
    connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID',
    illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY', louisiana: 'LA',
    maine: 'ME', maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
    mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
    'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
    'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK', oregon: 'OR',
    pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD',
    tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT', virginia: 'VA', washington: 'WA',
    'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY', ontario: 'ON', quebec: 'QC',
    'british columbia': 'BC', alberta: 'AB'
  };

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const miles = (a, b) => {
    const r = (d) => (d * Math.PI) / 180;
    const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
    return 3958.8 * 2 * Math.asin(Math.sqrt(h));
  };
  const loadAsset = (tag, attrs) => new Promise((resolve, reject) => {
    const el = Object.assign(document.createElement(tag), attrs, { onload: resolve, onerror: reject });
    document.head.appendChild(el);
  });

  class InstallerLocator extends HTMLElement {
    async connectedCallback() {
      this.status = this.querySelector('.locator__status');
      this.list = this.querySelector('[data-locator-list]');
      this.input = this.querySelector('input[type=search]');
      this.resetButton = this.querySelector('[data-locator-reset]');
      this.nearest = parseInt(this.dataset.nearest, 10) || 25;
      try {
        this.all = await this.loadAll();
      } catch (e) {
        this.status.textContent = 'Installers could not be loaded. Please refresh the page.';
        return;
      }
      this.all.forEach((i) => { i.lat = parseFloat(i.lat); i.lng = parseFloat(i.lng); i.hasPoint = !isNaN(i.lat) && !isNaN(i.lng); });
      this.all.sort((a, b) => (a.state || '').localeCompare(b.state || '') || (a.city || '').localeCompare(b.city || ''));
      if (this.dataset.showMap === 'true') this.initMap();
      let timer;
      this.input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => this.search(), 250); });
      this.querySelector('[data-locator-geo]').addEventListener('click', () => this.locate());
      this.resetButton.addEventListener('click', () => { this.input.value = ''; this.input.classList.remove('is-filled'); this.show(this.all); });
      this.show(this.all);
    }

    async loadAll() {
      const section = this.closest('.shopify-section') || document;
      const read = (root) => JSON.parse(root.querySelector('[data-installer-page]').textContent);
      const nextOf = (root) => { const n = root.querySelector('[data-installer-next]'); return n ? JSON.parse(n.textContent) : null; };
      const items = read(section);
      let next = nextOf(section);
      while (next) {
        const url = new URL(next, location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const doc = new DOMParser().parseFromString(await (await fetch(url)).text(), 'text/html');
        items.push(...read(doc));
        next = nextOf(doc);
      }
      return items;
    }

    search() {
      const q = this.input.value.trim().toLowerCase();
      if (!q) return this.show(this.all);
      if (/^\d{5}$/.test(q)) {
        const near = this.all.filter((i) => i.hasPoint && (i.zip || '').startsWith(q.slice(0, 3)));
        if (near.length) {
          const origin = { lat: near.reduce((s, i) => s + i.lat, 0) / near.length, lng: near.reduce((s, i) => s + i.lng, 0) / near.length };
          return this.showNearest(origin, `near ZIP ${q}`);
        }
      }
      const code = STATES[q] || (q.length === 2 ? q.toUpperCase() : null);
      const hits = this.all.filter((i) => (code && i.state === code)
        || [i.name, i.city, i.zip, i.address].some((v) => (v || '').toLowerCase().includes(q)));
      this.show(hits, hits.length ? null : `No installers match “${this.input.value.trim()}”. Try a nearby city or ZIP code, or use your location.`);
    }

    locate() {
      if (!navigator.geolocation) { this.status.textContent = 'Your browser cannot share its location. Search by ZIP code instead.'; return; }
      this.status.textContent = 'Finding your location…';
      navigator.geolocation.getCurrentPosition(
        (p) => this.showNearest({ lat: p.coords.latitude, lng: p.coords.longitude }, 'near you'),
        () => { this.status.textContent = 'Location is unavailable. Search by city, state or ZIP code instead.'; },
        { timeout: 10000 }
      );
    }

    showNearest(origin, label) {
      const ranked = this.all.filter((i) => i.hasPoint)
        .map((i) => ({ ...i, distance: miles(origin, i) }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, this.nearest);
      this.show(ranked, `${ranked.length} closest installers ${label}`, origin);
    }

    show(items, message, origin) {
      const total = this.all.length;
      this.status.textContent = message || (items.length === total ? `${total} installers` : `${items.length} of ${total} installers`);
      this.resetButton.hidden = items.length === total;
      this.list.innerHTML = items.map((i) => {
        const place = [i.address, [i.city, [i.state, i.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean);
        const dest = i.hasPoint ? `${i.lat},${i.lng}` : encodeURIComponent(`${i.name} ${place.join(' ')}`);
        return `<li class="locator__item">
          ${i.distance != null ? `<span class="locator__distance">${i.distance.toFixed(1)} mi</span>` : ''}
          <p class="locator__name">${esc(i.name)}</p>
          ${place.map((l) => `<p>${esc(l)}</p>`).join('')}
          <div class="locator__links">
            ${i.phone ? `<a class="link" href="tel:${esc(i.phone.replace(/[^\d+]/g, ''))}">${esc(i.phone)}</a>` : ''}
            ${i.website ? `<a class="link" href="${esc(i.website)}" target="_blank" rel="noopener">Website</a>` : ''}
            <a class="link" href="https://www.google.com/maps/dir/?api=1&destination=${dest}" target="_blank" rel="noopener">Directions</a>
          </div>
        </li>`;
      }).join('');
      this.plot(items, origin);
    }

    async initMap() {
      const el = this.querySelector('[data-locator-map]');
      if (!el) return;
      try {
        await loadAsset('link', { rel: 'stylesheet', href: LEAFLET + 'leaflet.min.css' });
        if (!window.L) await loadAsset('script', { src: LEAFLET + 'leaflet.min.js' });
      } catch (e) { el.hidden = true; return; }
      this.map = L.map(el, { scrollWheelZoom: false, fadeAnimation: false, zoomAnimation: false, markerZoomAnimation: false });
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);
      this.layer = L.layerGroup().addTo(this.map);
      this.plot(this.pending || this.all);
    }

    plot(items, origin) {
      if (!this.map) { this.pending = items; return; }
      this.layer.clearLayers();
      const points = items.filter((i) => i.hasPoint);
      points.forEach((i) => L.circleMarker([i.lat, i.lng], { radius: 6, weight: 1, color: '#000', fillColor: '#c8102e', fillOpacity: 0.9 })
        .bindPopup(`<strong>${esc(i.name)}</strong><br>${esc([i.city, i.state].filter(Boolean).join(', '))}`)
        .addTo(this.layer));
      if (origin) points.push(origin);
      if (points.length) this.map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [24, 24], maxZoom: 11 });
      else this.map.setView([39.8, -98.6], 4);
    }
  }

  customElements.define('installer-locator', InstallerLocator);
})();
